import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { getSession } from 'next-auth/react';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const CaliforniaCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="46% CAASPP Growth in Southern California | Curipod Case Study"
      subTitle="How a group of veteran teachers in Southern California finally found a tech tool that adds value to their instruction, driving a double-digit increase in student achievement."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>46% CAASPP Growth</HeroTitle>
            <HeroSubtitle>
              Targeted Writing Practice &amp; Immediate Feedback in Southern California
            </HeroSubtitle>
            <HeroDescription>
              How a group of veteran teachers in Southern California finally found a tech
              tool that adds value to their instruction, driving a double-digit increase
              in student achievement.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Southern California</MetaBadge>
              <MetaBadge>Upper Elementary ELA</MetaBadge>
              <MetaBadge>2023–2024 School Year</MetaBadge>
            </HeroMeta>
            <HeroCtaWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                </a>
              </Link>
              <Link href="/quotes?step=role" passHref>
                <a>
                  <HeroCtaButton type="primary">Book a demo</HeroCtaButton>
                </a>
              </Link>
            </HeroCtaWrapper>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    50<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Above or Near Standard</StatLabel>
                  <StatYear>2022–2023 · 5th Grade · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    73<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Above or Near Standard</StatLabel>
                  <StatYear>2023–2024 · 6th Grade · With Curipod</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem>
                  <StatNumber>
                    50<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Below Standard</StatLabel>
                  <StatYear>2022–2023</StatYear>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    27<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Below Standard</StatLabel>
                  <StatYear>2023–2024</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* DOWNLOAD CTA (below hero) */}
      <DownloadCtaBanner>
        <MaxWidthContainer>
          <DownloadCtaInner>
            <DownloadCtaLabel>
              Want the full story? Download the PDF version of this case study.
            </DownloadCtaLabel>
            <a
              href="https://curipod.com/Curipod_Case_Study_California.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="Curipod_Case_Study_California.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 44 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadCtaInner>
        </MaxWidthContainer>
      </DownloadCtaBanner>

      {/* THE STORY */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTag>THE STORY</SectionTag>
            <SectionTitle>Veteran Teachers Find a Tool That Works</SectionTitle>
            <SectionText>
              Over the years, a group of veteran teachers in Southern California routinely
              saw the latest and greatest EdTech sweep through the district, but they just
              didn&apos;t see the value in most tools. Then they saw a Curipod lesson.
              Once they experienced the authentic writing practice, immediate AI Feedback,
              and tangible impact on student engagement&mdash;plus, rising test
              scores&mdash;they were all in.
            </SectionText>

            <BlockQuote>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                In over 15 years as a tech trainer, I don&apos;t know that I&apos;ve seen
                a teacher site catch on as quickly for a wide range of tech abilities as
                Curipod.
              </QuoteText>
              <QuoteAttrib>— District Technology Training Specialist</QuoteAttrib>
            </BlockQuote>

            <SectionText style={{ marginTop: 32 }}>
              For about a month before state testing, upper elementary teachers added
              Curipod&apos;s AI-powered targeted writing practice and CAASPP Test Prep
              lessons into their routines. This wasn&apos;t a year-long test prep boot
              camp; just highly-effective and engaging practice.
            </SectionText>

            <BlockQuote>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                The students are engaged and are often asking to do more.
              </QuoteText>
              <QuoteAttrib>— Tech Coach</QuoteAttrib>
            </BlockQuote>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* HOW CURIPOD MADE A DIFFERENCE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              THE CURIPOD SHIFT
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Curipod Made a Difference in 3 Ways
            </SectionTitle>

            <ImpactGrid>
              <ImpactCard>
                <ImpactIcon>✍️</ImpactIcon>
                <ImpactTitle>Targeted, Engaging Writing Practice</ImpactTitle>
                <ImpactDescription>
                  Students respond to standards-aligned prompts modeled after CAASPP.
                  Powered by AI, they practice rigorous grade-level skills on topics they
                  care about, like supporting claims with evidence about Taylor Swift or
                  the World Cup.
                </ImpactDescription>
              </ImpactCard>

              <ImpactCard>
                <ImpactIcon>💬</ImpactIcon>
                <ImpactTitle>Immediate AI Feedback</ImpactTitle>
                <ImpactDescription>
                  With Curipod, students get personalized feedback from AI instantly.
                  Then, they have the chance to revise and improve their scores in real
                  time.
                </ImpactDescription>
              </ImpactCard>

              <ImpactCard $wide>
                <ImpactIcon>🤝</ImpactIcon>
                <ImpactTitle>Authentic Learning Community</ImpactTitle>
                <ImpactDescription>
                  What was once a solitary task becomes a social one. Writing becomes a
                  shared experience, where students discuss their feedback and celebrate
                  growth together.
                </ImpactDescription>
              </ImpactCard>
            </ImpactGrid>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* TEACHER PERSPECTIVE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTag>FROM THE TEACHERS</SectionTag>
            <SectionTitle>What Made the Difference</SectionTitle>

            <BlockQuote>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                Yes, those teachers worked hard, but they also attribute the growth to
                using Curipod.
              </QuoteText>
              <QuoteAttrib>— Tech Coach</QuoteAttrib>
            </BlockQuote>

            <SectionText style={{ marginTop: 32 }}>
              The teachers saw firsthand the power of AI in closing the feedback loop,
              which research shows is one of the most effective ways to drive outcomes.
              Even experienced teachers face the demands of the job, particularly around
              providing timely feedback on writing. One teacher shared that students were
              used to waiting a week or more for feedback. Curipod helps teachers achieve
              the impossible: provide personalized feedback to every student before the
              bell rings.
            </SectionText>

            <SectionText>
              What&apos;s even more notable is that these educators weren&apos;t
              tech-heavy users. In fact, the upper elementary teachers had little
              experience with EdTech tools. But Curipod&apos;s simple, visual design made
              it easy to get started.
            </SectionText>

            <BlockQuote>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                The teachers who never reach out to us are asking about Curipod. Once they
                try it, they come back and tell us, &lsquo;It&apos;s so easy!&rsquo;
              </QuoteText>
              <QuoteAttrib>— Tech Coach</QuoteAttrib>
            </BlockQuote>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* DETAILED RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              RESULTS THAT SPEAK FOR THEMSELVES
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              Detailed Breakdown
            </SectionTitle>

            <ResultsGrid>
              <ResultCard>
                <ResultCardTitle>2022–2023 (5th Grade)</ResultCardTitle>
                <ResultRow>
                  <ResultLabel>Above Standard</ResultLabel>
                  <BarContainer>
                    <Bar $width={5} $color={theme.colors.orange} />
                  </BarContainer>
                  <ResultValue>5%</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>Near Standard</ResultLabel>
                  <BarContainer>
                    <Bar $width={45} $color={theme.colors.orangeDark} />
                  </BarContainer>
                  <ResultValue>45%</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>Below Standard</ResultLabel>
                  <BarContainer>
                    <Bar $width={50} $color="rgba(255,255,255,0.2)" />
                  </BarContainer>
                  <ResultValue>50%</ResultValue>
                </ResultRow>
              </ResultCard>

              <ResultCard>
                <ResultCardTitle>2023–2024 (6th Grade)</ResultCardTitle>
                <ResultRow>
                  <ResultLabel>Above Standard</ResultLabel>
                  <BarContainer>
                    <Bar $width={13.5} $color={theme.colors.orange} />
                  </BarContainer>
                  <ResultValue>13.5%</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>Near Standard</ResultLabel>
                  <BarContainer>
                    <Bar $width={59.5} $color={theme.colors.orangeDark} />
                  </BarContainer>
                  <ResultValue>59.5%</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>Below Standard</ResultLabel>
                  <BarContainer>
                    <Bar $width={27} $color="rgba(255,255,255,0.2)" />
                  </BarContainer>
                  <ResultValue>27%</ResultValue>
                </ResultRow>
              </ResultCard>
            </ResultsGrid>

            <ResultNote>
              From the month of using Curipod, teachers saw a leap in writing achievement:
              73% of students scored Near or Above Standard, a growth of{' '}
              <strong>23 percentage points</strong>. Of those students, 13.5% scored Above
              Standard, a growth of <strong>8.5 percentage points</strong> from the year
              before. At the same time, the number of students below standard dropped from{' '}
              <strong>50% to just 27%</strong>.
            </ResultNote>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Raising Achievement, Closing Gaps
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Built on research-backed best practices for student engagement and learning,
              Curipod uses interactive tools and artificial intelligence to help teachers
              provide instruction that authentically engages students in learning. When
              students get more chances to write in ways they care about, and when
              teachers have the tools to empower student ownership, achievement soars.
            </SectionText>

            <FocusAreaGrid>
              {['Writing Practice', 'AI Feedback', 'Engagement', 'Achievement'].map(
                area => (
                  <FocusAreaBadge key={area}>{area}</FocusAreaBadge>
                ),
              )}
            </FocusAreaGrid>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                Curipod was helping to raise achievement and close the gaps.
              </FinalQuoteText>
            </FinalQuoteCard>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
              <Link href="/quotes?step=role" passHref>
                <a>
                  <Button
                    type="tertiary"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Book a demo
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIconWrap aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIconWrap>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the California case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="https://curipod.com/Curipod_Case_Study_California.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="Curipod_Case_Study_California.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );
  const session = await getSession({ req: context.req });
  return {
    props: {
      isAuthenticated: !!session,
      ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'marketing' })),
    },
  };
};

export default CaliforniaCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;

  @media ${MOBILE} {
    align-items: flex-start;
  }
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 20px;

  @media ${MOBILE} {
    gap: 20px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 48px;

  @media ${MOBILE} {
    width: 100%;
    height: 1px;
    margin: 0;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 16px 24px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 64px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 48px;
  }
`;

const StatPercent = styled.span`
  font-size: 28px;
  margin-top: 8px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── DOWNLOAD CTA BANNER (below hero) ──────────────────────────────────────── */

const DownloadCtaBanner = styled.div`
  background: ${theme.colors.beigeDark};
  border-bottom: 1px solid rgba(249, 151, 70, 0.2);
  padding: 16px 0;
`;

const DownloadCtaInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
`;

const DownloadCtaLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.text.dark};

  @media ${MOBILE} {
    font-size: 14px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── QUOTES ────────────────────────────────────────────────────────────────── */

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactDescription = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;
  max-width: 800px;
  margin-bottom: 48px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ResultCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 28px;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const ResultCardTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: ${theme.colors.white};
  margin-bottom: 20px;
  text-align: center;

  @media ${MOBILE} {
    font-size: 16px;
  }
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ResultLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  min-width: 95px;
  text-align: right;

  @media ${MOBILE} {
    min-width: 75px;
    font-size: 11px;
  }
`;

const BarContainer = styled.div`
  flex: 1;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
`;

const Bar = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  border-radius: 10px;
  transition: width 0.6s ease;
`;

const ResultValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.white};
  min-width: 45px;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-bottom: 40px;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
`;

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.orange};
  font-style: italic;
  line-height: 1.4;
  margin: 0;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIconWrap = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
